// The free-tier model the user reads through. Lifted from the long-form
// framework with three approved copy edits (B1, B2, B3 from the build plan).
// Italicised phrases are wrapped in *single asterisks* and rendered as <em>.

export type Department = {
  name: string;
  tagline: string;
  badgeText: string;
  badgeType: 'tier1' | 'tier2' | 'tier3' | 'merged' | 'new';
  absorbs?: string[];
  description: string;
  why: string;
  ctaText: string;
};

export type LegacyMapping = { old: string; new: string };

export type IntelligenceTierModel = {
  id: 4;
  name: 'Intelligence Tier';
  principle: string;
  deptCount: number;
  newCount: number;
  departments: Department[];
  legacyMap: LegacyMapping[];
};

export const intelligenceTier: IntelligenceTierModel = {
  id: 4,
  name: 'Intelligence Tier',
  principle:
    "Organise around the nature of intelligence being applied. Some work should run itself. Some needs human and AI in genuine fusion. Some requires judgment that cannot be delegated. Most organisations are doing all three badly, by accident, with no one accountable for the distinction.",
  deptCount: 7,
  newCount: 3,
  departments: [
    {
      name: 'Automated Systems',
      tagline: 'Runs itself. Humans audit, govern, and set thresholds',
      badgeText: 'Tier 1 · AI-Operated',
      badgeType: 'tier1',
      absorbs: [
        'Finance (transactional)',
        'IT Operations',
        'Customer Service (T1–2)',
        'HR Admin',
        'Supply Chain Execution',
        'Compliance Monitoring',
        'Basic Procurement',
      ],
      description:
        '*The fully AI-operated layer of the organisation.* Invoices processed, tickets resolved, schedules managed, compliance monitored, basic queries answered - without human involvement in the transaction itself. Humans in this tier are system designers, threshold-setters, and exception handlers. Thin, highly skilled, heavily governed.',
      // EDIT B1 applied - final sentence appended.
      why: "Most organisations are quietly automating these functions but embedding them in existing structures. Naming the tier creates honest accountability - how much of what we do is in this tier, what are the governance requirements, and what happens when it fails? When one of these systems fails publicly, the question 'whose decision was that?' becomes career-defining.",
      ctaText: "We're running this without naming it or governing it",
    },
    {
      name: 'Augmented Judgment',
      tagline: 'Where human judgment and AI analysis are genuinely fused - neither alone is sufficient',
      badgeText: 'Tier 2 · Human+AI',
      badgeType: 'tier2',
      absorbs: [
        'Marketing Strategy',
        'FP&A',
        'Risk Assessment',
        'Workforce Analytics',
        'Sales Strategy',
        'Ops Optimisation',
        'Product Management',
      ],
      description:
        "*The largest and most important tier.* AI generates the analysis, models the scenarios, and surfaces the patterns. Humans interpret, contextualise, and decide. The people here are intelligence interpreters, not analysis producers. They don't produce the insight; they govern its quality and make the call.",
      // EDIT B2 applied - entire "why" replaced.
      why:
        "Most organisations are already in this transition without naming it, which means it's happening unevenly, by accident, and badly governed. The bill arrives as duplicated tools, rework, and decisions no one will sign.",
      ctaText: "This transition is happening in our org by accident",
    },
    {
      name: 'Human Primacy',
      tagline: 'Where human judgment, trust, and accountability are irreplaceable',
      badgeText: 'Tier 3 · Human-Led',
      badgeType: 'tier3',
      absorbs: [
        'Executive Leadership',
        'Key Account Relationships',
        'Culture & Values',
        'Crisis Management',
        'Ethical Decision-Making',
        'Creative Direction',
      ],
      description:
        '*AI may inform but cannot substitute here.* These are the functions where human presence, judgment, and accountability are the product - not inputs to the product. A CEO navigating a crisis, a key account manager managing a relationship in distress, an ethics committee making a values call.',
      // EDIT B3 applied - final sentence appended.
      why:
        "Without this explicit tier, there is constant pressure to automate or augment functions that shouldn't be. Naming Human Primacy as a tier creates institutional protection for the judgment and relationship work that defines organisational character. The risk is the inverse of what's debated: not that human judgment will be replaced, but that no one is drawing the line, so judgment is being quietly displaced by analysis without anyone noticing.",
      ctaText: "No one in my org is drawing this line",
    },
    {
      name: 'Intelligence Architecture',
      tagline: 'Designing how the three tiers interact - and governing migration between them',
      badgeText: 'New Function',
      badgeType: 'new',
      description:
        "*The meta-function.* Decides what goes in which tier. Manages the continuous migration of tasks from Human Primacy to Augmented to Automated as AI capability improves. Governs the organisation's intelligence structure itself - not what the AI does, but how the organisation is designed to use it. Reports directly to the CEO. Arguably the most consequential new role in any large organisation.",
      why:
        'Currently, AI deployment decisions are made by IT, individual business units, or vendors - with no coherent view of the whole. Intelligence Architecture creates that coherent view and makes it a governance responsibility rather than a technology project.',
      ctaText: "No one has the coherent view in my org",
    },
    {
      name: 'Frontier Sensing',
      tagline: "What can AI do now that it couldn't last quarter - and what does that mean for our structure?",
      badgeText: 'New Function',
      badgeType: 'new',
      description:
        '*Continuously monitors the AI capability frontier and translates it into structural implications.* When a new model ships that changes what is possible in legal analysis, Frontier Sensing identifies it and feeds a recommendation to Intelligence Architecture: does something need to migrate between tiers? The pace of AI development means this runs continuously, not annually.',
      why:
        'No organisation currently has a function whose explicit job is to watch AI capability development and translate it into org-design implications in near-real time. Most do this ad hoc, or not at all. As the capability frontier moves monthly, ad hoc is too slow.',
      ctaText: "We're watching this ad hoc, or not at all",
    },
    {
      name: 'Human Development',
      tagline: 'Developing humans specifically for what AI cannot do',
      badgeText: 'New Function',
      badgeType: 'new',
      description:
        'Not skills training. *Human flourishing, deliberately designed.* Develops people for judgment, relationship quality, ethical reasoning, contextual wisdom, and creative originality - the capabilities that sit firmly in Human Primacy and cannot migrate to Automated. In a world where AI handles analytical heavy lifting, what makes a human irreplaceable becomes a specific, learnable set of capabilities.',
      why:
        "Current L&D teaches skills that are often being automated. Human Development teaches the capabilities that survive automation - and deliberately does not teach the ones that don't. A fundamentally different curriculum built from a fundamentally different premise.",
      ctaText: "We're still running L&D, not this",
    },
    {
      name: 'Value Realisation',
      tagline: 'Are we actually getting better at decisions that create value - and can we prove it?',
      badgeText: 'Merged',
      badgeType: 'merged',
      absorbs: [
        'Finance (strategic)',
        'Strategy',
        'Commercial',
        'Business Intelligence',
        'Performance Management',
      ],
      description:
        '*Connects intelligence investment to value creation.* Holds the single most important question in an AI-era organisation: is our increasing dependence on AI systems making us better at the decisions that matter? Measures decision quality, not just outcomes. Governs the ROI on intelligence - human and artificial.',
      why:
        "Finance, strategy, and business intelligence all try to answer 'is this working?' Unified under one accountability - value realisation - they measure the thing that actually matters: decision quality leading to organisational value.",
      ctaText: "We can't prove our intelligence investment is working",
    },
  ],
  legacyMap: [
    { old: 'Finance (transactional)', new: 'Automated Systems' },
    { old: 'Finance (strategic)', new: 'Value Realisation' },
    { old: 'Marketing', new: 'Augmented Judgment' },
    { old: 'Sales', new: 'Augmented Judgment + Human Primacy' },
    { old: 'HR Admin', new: 'Automated Systems' },
    { old: 'HR Strategy / L&D', new: 'Human Development' },
    { old: 'Legal', new: 'Augmented Judgment + Human Primacy' },
    { old: 'Risk', new: 'Augmented Judgment' },
    { old: 'Strategy', new: 'Value Realisation + Human Primacy' },
    { old: 'IT', new: 'Automated Systems + Intelligence Architecture' },
    { old: 'Operations', new: 'Automated Systems' },
    { old: 'R&D / Innovation', new: 'Frontier Sensing' },
    { old: 'Product Management', new: 'Augmented Judgment' },
    { old: 'Customer Service', new: 'Automated Systems + Human Primacy' },
    { old: 'Data / Analytics', new: 'Intelligence Architecture' },
    { old: 'Executive Team', new: 'Human Primacy' },
  ],
};
